// Email template builder. Block-based editor with live HTML preview.
// Templates feed into workflow email steps + ad-hoc broadcasts.
import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Field, Input, Textarea, Select, Modal, Spinner, Badge } from '../ui.jsx';

const BLOCK_LABELS = {
  hero:'🎯 Hero', text:'📝 Text', button:'🔘 Button', image:'🖼 Image',
  divider:'➖ Divider', spacer:'⬜ Spacer', footer:'🦶 Footer',
};

export function EmailTemplates({ toast }) {
  const [list, setList] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');

  const load = () => api('/api/email-templates').then(d => { setList(d); if (!activeId && d[0]) setActiveId(d[0].id); }).catch(()=>setList([]));
  useEffect(() => { load(); }, []);

  const newTemplate = async () => {
    const name = prompt('Template name (e.g. "New lead welcome")');
    if (!name?.trim()) return;
    try {
      const r = await api('/api/email-templates', { method:'POST', body:{ name, subject:'', blocks:[
        { id:'h-'+Math.random().toString(36).slice(2,8), type:'hero', props:{ headline:'Welcome!', sub:'Quick note from the team.' }},
        { id:'t-'+Math.random().toString(36).slice(2,8), type:'text', props:{ body:'Your message here.' }},
        { id:'b-'+Math.random().toString(36).slice(2,8), type:'button', props:{ label:'Get started', href:'#' }},
        { id:'f-'+Math.random().toString(36).slice(2,8), type:'footer', props:{ company:'Your business', phone:'' }},
      ]}});
      await load(); setActiveId(r.id);
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!list) return <div style={{ padding:40 }}><Spinner sz={32}/></div>;
  const active = list.find(t => t.id === activeId);

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>✉️ Email Templates</h1>
      <Btn v="primary" onClick={newTemplate}>+ New Template</Btn>
    </div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>
      Drag-friendly email builder. Templates can be referenced from workflow Email steps.
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'240px 1fr 1fr', gap:14 }}>
      <div style={{ ...styles.card, padding:8, alignSelf:'flex-start', position:'sticky', top:14 }}>
        <div style={{ padding:'6px 8px', fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>Templates</div>
        {list.length === 0 && <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>None yet — create one →</div>}
        {list.map(t => (
          <div key={t.id} onClick={()=>setActiveId(t.id)} style={{
            padding:'8px 10px', borderRadius:8, cursor:'pointer',
            background: t.id === activeId ? G.purpleDim : 'transparent',
            borderLeft: `3px solid ${t.id === activeId ? G.purple : 'transparent'}`,
            marginBottom:2,
          }}>
            <div style={{ fontSize:13, fontWeight:600 }}>{t.name}</div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{t.blocks.length} blocks</div>
          </div>
        ))}
      </div>

      {active ? <Editor key={active.id} t={active} onChanged={load} onPreview={setPreviewHtml} toast={toast}/>
              : <div style={{ ...styles.card, padding:60, textAlign:'center', color:G.textMuted, gridColumn:'span 2' }}>Pick a template to edit, or create one.</div>}

      {active && <Preview html={previewHtml} templateId={active.id}/>}
    </div>
  </div>;
}

function Editor({ t, onChanged, onPreview, toast }) {
  const [name, setName] = useState(t.name);
  const [subject, setSubject] = useState(t.subject || '');
  const [blocks, setBlocks] = useState(t.blocks);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  useEffect(() => { setName(t.name); setSubject(t.subject||''); setBlocks(t.blocks); setDirty(false); }, [t.id]);
  // Push preview HTML up whenever we change the blocks
  useEffect(() => { onPreview('/api/email-templates/'+t.id+'/preview?t='+Date.now()); }, [t.id]);

  const save = async () => {
    try { await api(`/api/email-templates/${t.id}`, { method:'PATCH', body:{ name, subject, blocks }}); setDirty(false); onChanged(); toast('✓ Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const del = async () => {
    if (!confirm('Delete template?')) return;
    try { await api(`/api/email-templates/${t.id}`, { method:'DELETE' }); onChanged(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const update = (i, props) => { setBlocks(blocks.map((b,j) => j===i?{...b, props:{...b.props, ...props}}:b)); setDirty(true); };
  const remove = (i) => { setBlocks(blocks.filter((_,j) => j!==i)); setDirty(true); };
  const move = (i, dir) => { const j=i+dir; if (j<0||j>=blocks.length) return; const next=[...blocks]; [next[i],next[j]]=[next[j],next[i]]; setBlocks(next); setDirty(true); };
  const add = (type) => { setBlocks([...blocks, { id:type+'-'+Math.random().toString(36).slice(2,8), type, props:defaults(type) }]); setAdding(false); setDirty(true); };
  const sendTest = async () => {
    const to = prompt('Send test to which email?');
    if (!to) return;
    try { await api(`/api/email-templates/${t.id}/send-test`, { method:'POST', body:{ to }}); toast(`✓ Sent test to ${to}`); }
    catch (e) { toast(e.message, 'error'); }
  };

  return <div>
    <div style={{ ...styles.card, padding:14, marginBottom:10 }}>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr auto auto auto', gap:10, alignItems:'flex-end' }}>
        <Field label="Name"><Input value={name} onChange={e=>{setName(e.target.value); setDirty(true);}}/></Field>
        <Field label="Subject line"><Input value={subject} onChange={e=>{setSubject(e.target.value); setDirty(true);}}/></Field>
        <Btn sz="sm" onClick={sendTest}>📧 Test</Btn>
        <Btn sz="sm" v="danger" onClick={del}>🗑</Btn>
        <Btn sz="sm" v="primary" onClick={save} disabled={!dirty}>{dirty?'💾':'Saved'}</Btn>
      </div>
    </div>
    {blocks.map((b,i) => <BlockCard key={b.id} block={b} idx={i} count={blocks.length} onUpdate={update} onRemove={remove} onMove={move}/>)}
    <div style={{ ...styles.card, padding:12, textAlign:'center', borderStyle:'dashed' }}>
      {adding
        ? <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center' }}>
            {Object.entries(BLOCK_LABELS).map(([k,l]) => <Btn key={k} sz="sm" onClick={()=>add(k)}>{l}</Btn>)}
            <Btn sz="sm" v="ghost" onClick={()=>setAdding(false)}>Cancel</Btn>
          </div>
        : <Btn v="primary" onClick={()=>setAdding(true)}>＋ Add block</Btn>}
    </div>
  </div>;
}

function defaults(type) {
  switch (type) {
    case 'hero':    return { headline:'Big headline', sub:'Sub-line' };
    case 'text':    return { headline:'', body:'Your text here.' };
    case 'button':  return { label:'Click here', href:'#', color:'#a78bfa', align:'center' };
    case 'image':   return { url:'', alt:'' };
    case 'divider': return {};
    case 'spacer':  return { size:20 };
    case 'footer':  return { company:'Your business', phone:'', address:'' };
    default:        return {};
  }
}

function BlockCard({ block, idx, count, onUpdate, onRemove, onMove }) {
  const p = block.props || {};
  return <div style={{ ...styles.card, padding:12, marginBottom:8 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
      <strong style={{ fontSize:12 }}>{BLOCK_LABELS[block.type] || block.type}</strong>
      <div style={{ display:'flex', gap:4 }}>
        <Btn sz="xs" disabled={idx===0} onClick={()=>onMove(idx, -1)}>↑</Btn>
        <Btn sz="xs" disabled={idx===count-1} onClick={()=>onMove(idx, +1)}>↓</Btn>
        <Btn sz="xs" v="danger" onClick={()=>onRemove(idx)}>×</Btn>
      </div>
    </div>
    {block.type === 'hero' && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
      <Field label="Headline" style={{gridColumn:'1/-1'}}><Input value={p.headline||''} onChange={e=>onUpdate(idx,{headline:e.target.value})}/></Field>
      <Field label="Sub" style={{gridColumn:'1/-1'}}><Input value={p.sub||''} onChange={e=>onUpdate(idx,{sub:e.target.value})}/></Field>
    </div>}
    {block.type === 'text' && <div>
      <Field label="Heading (optional)"><Input value={p.headline||''} onChange={e=>onUpdate(idx,{headline:e.target.value})}/></Field>
      <Field label="Body"><Textarea value={p.body||''} onChange={e=>onUpdate(idx,{body:e.target.value})} rows={4}/></Field>
    </div>}
    {block.type === 'button' && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 90px 90px', gap:8 }}>
      <Field label="Label"><Input value={p.label||''} onChange={e=>onUpdate(idx,{label:e.target.value})}/></Field>
      <Field label="Link"><Input value={p.href||''} onChange={e=>onUpdate(idx,{href:e.target.value})}/></Field>
      <Field label="Color"><Input type="color" value={p.color||'#a78bfa'} onChange={e=>onUpdate(idx,{color:e.target.value})}/></Field>
      <Field label="Align"><Select value={p.align||'center'} onChange={e=>onUpdate(idx,{align:e.target.value})}><option>center</option><option>left</option><option>right</option></Select></Field>
    </div>}
    {block.type === 'image' && <div>
      <Field label="Image URL"><Input value={p.url||''} onChange={e=>onUpdate(idx,{url:e.target.value})}/></Field>
      <Field label="Alt text"><Input value={p.alt||''} onChange={e=>onUpdate(idx,{alt:e.target.value})}/></Field>
    </div>}
    {block.type === 'spacer' && <Field label="Size (px)"><Input type="number" value={p.size||20} onChange={e=>onUpdate(idx,{size:Number(e.target.value)})}/></Field>}
    {block.type === 'footer' && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
      <Field label="Company"><Input value={p.company||''} onChange={e=>onUpdate(idx,{company:e.target.value})}/></Field>
      <Field label="Phone"><Input value={p.phone||''} onChange={e=>onUpdate(idx,{phone:e.target.value})}/></Field>
      <Field label="Address" style={{gridColumn:'1/-1'}}><Input value={p.address||''} onChange={e=>onUpdate(idx,{address:e.target.value})}/></Field>
    </div>}
  </div>;
}

function Preview({ html, templateId }) {
  // Force refresh on save by adding a query param
  return <div style={{ ...styles.card, padding:0, height:'70vh', overflow:'hidden', alignSelf:'flex-start', position:'sticky', top:14 }}>
    <div style={{ padding:'10px 14px', borderBottom:`1px solid ${G.border}`, fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Preview</div>
    <iframe src={`/api/email-templates/${templateId}/preview`} style={{ width:'100%', height:'calc(70vh - 36px)', border:0, background:'#f8fafc' }}/>
  </div>;
}
